import React, { useState } from 'react';
import {
    Dialog,
    Paper,
    CircularProgress,
    Typography,
    Button,
    withStyles,
    Tooltip,
    IconButton,
} from '@material-ui/core';
import { CloudDownload } from '@material-ui/icons';
import { DropzoneArea } from 'material-ui-dropzone';
import { connect } from 'react-redux';
import { BackButton, capitalize } from '../../sc-utilities';
import History from '../../../History';
import * as actions from './store/skuMappingActions';
import { snackActions } from '../../sc-utilities/SnackBar';
import { externalDownload } from '../../sc-utilities/download.utils';
import config from '../../../config/config';

const styles = (theme) => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        paddingRight: theme.spacing(6),
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            paddingRight: 0,
        },
    },
    dialog: {
        width: 'auto',
        overflow: 'hidden',
    },
    paper: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        overflow: 'hidden',
        alignItems: 'center',
    },
    progress: {
        width: `398px`,
        height: '400px',
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        marginTop: '10px',
    },
    typographyTo: {
        marginTop: '5px',
        color: theme.palette.buttonText.default,
    },
    typography: {
        marginTop: '5px',
        color: theme.palette.buttonText.default,
        position: 'absolute',
        top: '40%',
        right: '0',
        textAlign: 'center',
        left: '0',
    },
});

const SKUMappingUpload = (props) => {
    let { classes, translations, skuMapping, activeCustomer } = props;
    const [fileToSend, setFileToSend] = useState({ fileToSend: {}, fileName: '', buttonDisabled: true, open: false });
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (
            !fileToSend.fileName ||
            (!fileToSend.fileName.includes('.xlsx') &&
                !fileToSend.fileName.includes('.xls') &&
                !fileToSend.fileName.includes('.json'))
        ) {
            return snackActions.snackBar({
                message: translations['choose_xlsx_or_json'] || 'Please choose xlsx or json file',
                options: {
                    variant: 'warning',
                },
            });
        }
        setFileToSend({ ...fileToSend, buttonDisabled: true });
        const data = new FormData();
        data.append('File', fileToSend.fileToSend);
        data.append('ActiveCustomerID', activeCustomer);
        let token = localStorage.getItem('SESSION');

        data.append('Token', token);
        let res = await props.uploadSKUMappings(data);
        if (res) {
            History.goBack();
        }
    };

    const handleUpload = (event) => {
        let file = event[0];
        if (!file) {
            setFileToSend({ ...fileToSend, fileToSend: {}, fileName: '', buttonDisabled: true });
            return;
        }
        setFileToSend({ ...fileToSend, fileToSend: file, fileName: file.name, buttonDisabled: false });
    };

    const handleDownload = async (extension) => {
        const objectURL = `${config.web}/assets/example-sku-mappings.${extension}`;
        let exportDataSuccessful = await externalDownload(objectURL);
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(
                translations['download_error_msg'] || `Could not download file `,
                'error'
            );
        }
    };

    return (
        <>
            <Paper className={classes.root}>
                {BackButton(translations)}

                <Typography>
                    {translations['supported_formats'] || 'Supported formats'}: .xlsx, .xls, .json.
                    <br />
                </Typography>
                <Tooltip
                    title={
                        translations['download_example_sku_mapping_xlsx'] || 'Download .xlsx example SKU mapping file'
                    }
                >
                    <IconButton
                        component="div"
                        aria-label={
                            translations['download_example_sku_mapping_xlsx'] ||
                            'Download .xlsx example SKU mapping file'
                        }
                        onClick={() => handleDownload('xlsx')}
                    >
                        {<CloudDownload />}
                    </IconButton>
                </Tooltip>
                <Tooltip
                    title={
                        translations['download_example_sku_mapping_json'] || 'Download .json example SKU mapping file'
                    }
                >
                    <IconButton
                        component="div"
                        aria-label={
                            translations['download_example_sku_mapping_json'] ||
                            'Download .json example SKU mapping file'
                        }
                        onClick={() => handleDownload('json')}
                    >
                        {<CloudDownload />}
                    </IconButton>
                </Tooltip>
                <form>
                    <DropzoneArea
                        filesLimit={1}
                        maxFileSize={5368709120}
                        getFileRemovedMessage={(fileName) => {
                            const text = translations
                                ? capitalize(
                                      `${translations['file'] || 'File'} ${fileName} ${
                                          translations['removed'] || 'removed'
                                      }.`
                                  )
                                : `File ${fileName} removed.`;

                            return text;
                        }}
                        getFileAddedMessage={(fileName) => {
                            const text = translations
                                ? capitalize(
                                      `${translations['file'] || 'File'} ${fileName} ${
                                          translations['successfully_added'] || 'successfully added'
                                      }.`
                                  )
                                : `File ${fileName} successfully added.`;

                            return text;
                        }}
                        getFileLimitExceedMessage={(filesLimit) => {
                            const text = translations
                                ? capitalize(
                                      `${
                                          translations['maximum_allowed_number'] ||
                                          'Maximum allowed number of files exceeded. Only'
                                      } ${filesLimit} ${translations['allowed'] || 'allowed'}.`
                                  )
                                : `Maximum allowed number of files exceeded. Only ${filesLimit} allowed`;

                            return text;
                        }}
                        getDropRejectMessage={(rejectedFile) => {
                            const text = translations
                                ? capitalize(
                                      `${translations['file'] || 'File'} ${
                                          translations['was_rejected'] || 'was rejected'
                                      }.`
                                  )
                                : `File was rejected.`;

                            return text;
                        }}
                        dropzoneText={`${translations['drag_and_drop_an'] || 'Drag and drop an'} .xlsx or .json ${
                            translations['file_here'] || 'file here or click'
                        }`}
                        onChange={handleUpload}
                        acceptedFiles={['.xlsx', '.xls', '.json']}
                    />
                    <Button
                        className={classes.submit}
                        variant="contained"
                        type="submit"
                        onClick={handleSubmit}
                        fullWidth={true}
                        disabled={fileToSend.buttonDisabled}
                    >
                        {translations['import'] || 'Import'}
                    </Button>
                </form>
            </Paper>
            <Dialog
                disableBackdropClick={true}
                disableEscapeKeyDown={false}
                open={skuMapping.snackStart ? true : false}
                PaperProps={{
                    classes: {
                        root: classes.paper,
                    },
                }}
            >
                <div style={{ marginBottom: '1px', position: 'relative' }}>
                    <CircularProgress
                        variant="static"
                        color="primary"
                        style={{ marginBottom: '10px' }}
                        size={300}
                        value={
                            skuMapping.progress && skuMapping.progress > 100
                                ? 100
                                : skuMapping.progress
                                ? skuMapping.progress
                                : 0
                        }
                    />
                    <Typography className={classes.typography} variant="h4">
                        {' '}
                        {skuMapping.progress && skuMapping.progress > 100
                            ? 100
                            : skuMapping.progress
                            ? skuMapping.progress
                            : 0}
                        %{' '}
                    </Typography>
                    <br></br>
                </div>
                {skuMapping.progress === 100 || skuMapping.progress > 100 ? (
                    <Typography variant="h5" className={classes.typographyTo}>
                        {translations['file_upload'] || 'File upload finished'} <br />
                        {translations['import_progress'] || 'Import in progress'}
                    </Typography>
                ) : (
                    <Typography variant="h5" className={classes.typographyTo}>
                        {translations['please_do_not'] || 'Please do not refresh THIS page'}
                    </Typography>
                )}
            </Dialog>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        skuMapping: state.setting.skuMappings,
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        uploadSKUMappings: (data) => dispatch(actions.uploadSKUMappings(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(SKUMappingUpload));
